import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Icons } from "../components/Icons";
import { getAnnouncementImageUrl } from "../services/api";

const OrgDashboardMobile = ({
  userName,
  isOffCampusUser,
  academicYear,
  timeLeft,
  stats,
  recentDocs,
  announcements,
  activeChecklist,
  complianceItems,
  userAvatar,
}) => {
  const navigate = useNavigate();

  const [greeting] = useState(() => {
    const greetings = ["Good day", "Welcome back", "Hello", "Greetings", "Hi there"];
    return greetings[Math.floor(Math.random() * greetings.length)];
  });

  const totalSlots = stats.totalSlots || 10;
  const calcPct = (val) => (totalSlots > 0 ? Math.min(100, Math.round((val / totalSlots) * 100)) : 0);

  // File badge helper
  const getFileBadge = (filePath) => {
    const path = (filePath || "").toLowerCase();
    if (path.endsWith(".pdf"))
      return { label: "PDF", color: "text-red-500 bg-red-50 border-red-100" };
    if (path.endsWith(".doc") || path.endsWith(".docx"))
      return { label: "DOC", color: "text-blue-500 bg-blue-50 border-blue-100" };
    if (path.endsWith(".png") || path.endsWith(".jpg") || path.endsWith(".jpeg"))
      return { label: "IMG", color: "text-purple-500 bg-purple-50 border-purple-100" };
    return { label: "FILE", color: "text-gray-500 bg-gray-50 border-gray-100" };
  };

  const getStatusBadge = (status) => {
    const map = {
      reviewed: { label: "Reviewed", className: "bg-[#e0f5ee] text-[#10B981]" },
      for_revision: { label: "Revision", className: "bg-orange-100 text-orange-600" },
      rejected: { label: "Rejected", className: "bg-red-100 text-red-600" },
      pending: { label: "Pending", className: "bg-yellow-100 text-yellow-700" },
    };
    return map[status] || { label: status, className: "bg-gray-100 text-gray-600" };
  };

  const primaryTargetUrl = isOffCampusUser ? "/org/off-campus" : "/org/accreditation";
  const portalName = isOffCampusUser ? "Off-Campus Activity" : "Accreditation";

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#d1f9ec] via-[#f0fdf9] to-[#f4f7f6] pb-32">
      <div className="p-5 pt-8">
        {/* Greetings */}
        <div className="mb-1">
          <p className="text-[20px] text-[#054334]">
            {greeting}, <span className="font-bold">{userName}!</span>
          </p>
        </div>

        {/* Academic Year */}
        <div className="mb-4">
          <p className="text-[11px] text-gray-500 font-medium px-1">
            {academicYear ? `${academicYear.year_label} · ${portalName} Period` : `${portalName} Period`}
          </p>
        </div>

        {/* Lock Date Countdown Card — For Accreditation / Re-accreditation Users (Hidden for Off-Campus) */}
        {!isOffCampusUser && academicYear && (
          <div className="bg-[#0D3B2E] rounded-[14px] p-3.5 text-white shadow-md relative overflow-hidden mb-4">
            {/* Top row: Label, Date & Submissions status */}
            <div className="flex items-center justify-between gap-2 z-10 relative">
              <div className="min-w-0">
                <p className="text-[9px] text-emerald-200/80 tracking-wider uppercase font-semibold">
                  Cycle Lock Date — {academicYear.year_label}
                </p>
                <h3 className="text-[17px] font-extrabold tracking-tight text-white leading-tight mt-0.5">
                  {new Date(academicYear.lock_date).toLocaleDateString("en-US", {
                    month: "long",
                    day: "numeric",
                    year: "numeric",
                  })}
                </h3>
              </div>
              <div
                className={`inline-flex items-center space-x-1 text-[10px] px-2.5 py-0.5 rounded-full border flex-shrink-0 self-center ${new Date(academicYear.lock_date) > new Date()
                    ? "bg-green-500/20 text-green-400 border-green-500/30"
                    : "bg-red-500/20 text-red-400 border-red-500/30"
                  }`}
              >

                <span className="font-semibold text-[10px]">
                  {new Date(academicYear.lock_date) > new Date()
                    ? "Submissions Open"
                    : "Submissions Closed"}
                </span>
              </div>
            </div>

            {/* Bottom row: Compact timer blocks */}
            <div className="grid grid-cols-4 gap-2 mt-2.5 pt-2.5 border-t border-white/10 z-10 relative">
              {(timeLeft || ["00", "00", "00", "00"]).map((val, idx) => {
                const labels = ["DAYS", "HOURS", "MINS", "SECS"];
                return (
                  <div key={idx} className="bg-[#00A358] rounded-lg py-1 px-1 flex flex-col items-center justify-center shadow-xs">
                    <span className="text-[16px] font-black leading-tight">{val}</span>
                    <span className="text-[8px] text-emerald-100/90 font-bold tracking-wider uppercase mt-0.5">
                      {labels[idx]}
                    </span>
                  </div>
                );
              })}
            </div>

            {/* Decorative elements */}
            <div className="absolute -right-6 -top-6 w-24 h-24 bg-white/5 rounded-full blur-xl pointer-events-none"></div>
            <div className="absolute -left-6 -bottom-6 w-20 h-20 bg-primary/40 rounded-full blur-lg pointer-events-none"></div>
          </div>
        )}

        {/* Stat Cards Grid (2x2) */}
        <div className="grid grid-cols-2 gap-4 mb-6">
          {/* Pending */}
          <div className="bg-white rounded-[16px] p-4 shadow-sm border border-gray-100/50 flex flex-col h-[130px]">
            <div>
              <p className="text-[13px] font-bold text-gray-700">Pending</p>
            </div>
            <div className="flex-1 flex items-center">
              <h3 className="text-[42px] font-black text-[#054334] leading-none tracking-tighter">{stats.pendingCount}</h3>
            </div>
            <div className="mt-auto">
              <div className="w-full h-1.5 rounded-full overflow-hidden flex bg-gray-100 mb-1.5">
                <div className="h-full bg-yellow-400" style={{ width: `${calcPct(stats.pendingCount)}%` }}></div>
              </div>
              <div className="flex justify-between">
                <span className="text-[8px] font-bold text-gray-400">{stats.pendingCount} of {totalSlots} docs</span>
                <span className="text-[8px] font-bold text-gray-600">{calcPct(stats.pendingCount)}%</span>
              </div>
            </div>
          </div>

          {/* Reviewed */}
          <div className="bg-white rounded-[16px] p-4 shadow-sm border border-gray-100/50 flex flex-col h-[130px]">
            <div>
              <p className="text-[13px] font-bold text-gray-700">Reviewed</p>
            </div>
            <div className="flex-1 flex items-center">
              <h3 className="text-[42px] font-black text-[#054334] leading-none tracking-tighter">{stats.reviewedCount}</h3>
            </div>
            <div className="mt-auto">
              <div className="w-full h-1.5 rounded-full overflow-hidden flex bg-gray-100 mb-1.5">
                <div className="h-full bg-green-500" style={{ width: `${calcPct(stats.reviewedCount)}%` }}></div>
              </div>
              <div className="flex justify-between">
                <span className="text-[8px] font-bold text-gray-400">{stats.reviewedCount} of {totalSlots} docs</span>
                <span className="text-[8px] font-bold text-gray-600">{calcPct(stats.reviewedCount)}%</span>
              </div>
            </div>
          </div>

          {/* For Revision */}
          <div className="bg-white rounded-[16px] p-4 shadow-sm border border-gray-100/50 flex flex-col h-[130px]">
            <div>
              <p className="text-[13px] font-bold text-gray-700">For Revision/s</p>
            </div>
            <div className="flex-1 flex items-center">
              <h3 className="text-[42px] font-black text-[#054334] leading-none tracking-tighter">{stats.revisionCount}</h3>
            </div>
            <div className="mt-auto">
              <div className="w-full h-1.5 rounded-full overflow-hidden flex bg-gray-100 mb-1.5">
                <div className="h-full bg-orange-500" style={{ width: `${calcPct(stats.revisionCount)}%` }}></div>
              </div>
              <div className="flex justify-between">
                <span className="text-[8px] font-bold text-gray-400">{stats.revisionCount} of {totalSlots} docs</span>
                <span className="text-[8px] font-bold text-gray-600">{calcPct(stats.revisionCount)}%</span>
              </div>
            </div>
          </div>

          {/* Rejected */}
          <div className="bg-white rounded-[16px] p-4 shadow-sm border border-gray-100/50 flex flex-col h-[130px]">
            <div>
              <p className="text-[13px] font-bold text-gray-700">Rejected</p>
            </div>
            <div className="flex-1 flex items-center">
              <h3 className="text-[42px] font-black text-[#054334] leading-none tracking-tighter">{stats.rejectedCount}</h3>
            </div>
            <div className="mt-auto">
              <div className="w-full h-1.5 rounded-full overflow-hidden flex bg-gray-100 mb-1.5">
                <div className="h-full bg-red-500" style={{ width: `${calcPct(stats.rejectedCount)}%` }}></div>
              </div>
              <div className="flex justify-between">
                <span className="text-[8px] font-bold text-gray-400">{stats.rejectedCount} of {totalSlots} docs</span>
                <span className="text-[8px] font-bold text-gray-600">{calcPct(stats.rejectedCount)}%</span>
              </div>
            </div>
          </div>
        </div>

        {/* Recent Submissions */}
        <div className="bg-white rounded-[16px] shadow-sm mb-6 overflow-hidden border border-gray-100">
          <div className="bg-[#054334] px-4 py-3 flex justify-between items-center">
            <h2 className="text-white font-bold text-[13px]">
              {isOffCampusUser ? "Recent Off-Campus Activity Submissions" : "Recent Accreditation Submissions"}
            </h2>
            <button onClick={() => navigate(primaryTargetUrl)} className="text-white text-[9px] font-bold uppercase tracking-wider flex items-center">
              View All <span className="ml-1">→</span>
            </button>
          </div>
          <div className="px-4 py-2 border-b border-gray-100 bg-gray-50/50 flex justify-between">
            <span className="text-[10px] font-bold text-[#054334] uppercase w-1/2">Document</span>
            <div className="flex w-1/2 justify-between">
              <span className="text-[10px] font-bold text-[#054334] uppercase text-left w-1/2">Date</span>
              <span className="text-[10px] font-bold text-[#054334] uppercase text-right w-1/2">Status</span>
            </div>
          </div>
          <div className="divide-y divide-gray-100">
            {recentDocs.length === 0 ? (
              <div className="p-4 text-center text-[12px] text-gray-400">No documents yet.</div>
            ) : (
              recentDocs.slice(0, 3).map((doc, idx) => {
                const badge = getFileBadge(doc.file_path);
                const status = getStatusBadge(doc.status);
                return (
                  <div key={doc.accreditation_document_id || doc.offcampus_document_id || idx} onClick={() => navigate(primaryTargetUrl)} className="px-4 py-3 flex justify-between items-center bg-white cursor-pointer hover:bg-gray-50 transition-colors">
                    <div className="flex items-center space-x-2 w-1/2 overflow-hidden pr-2">
                      <span className={`text-[8px] font-black px-1 py-0.5 rounded border flex-shrink-0 ${badge.color}`}>
                        {badge.label}
                      </span>
                      <span className="text-[12px] font-medium text-gray-700 truncate">{doc.document_name}</span>
                    </div>
                    <div className="flex w-1/2 justify-between items-center">
                      <span className="text-[12px] text-gray-600 w-1/2 text-left whitespace-nowrap">
                        {doc.submitted_at ? new Date(doc.submitted_at).toLocaleDateString("en-US", { month: "short", day: "numeric" }) : "—"}
                      </span>
                      <span className={`${status.className} px-1.5 py-0.5 rounded-full text-[9px] font-bold whitespace-nowrap`}>
                        {status.label}
                      </span>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>

        {/* Compliance Progress */}
        <div className="bg-white rounded-[16px] shadow-sm mb-6 overflow-hidden border border-gray-100">
          <div className="bg-[#054334] px-4 py-3 flex justify-between items-center">
            <h2 className="text-white font-bold text-[13px]">Compliance Progress</h2>
            <span className="text-white font-bold text-[13px]">
              {activeChecklist ? `${activeChecklist.submitted}/${activeChecklist.total_required}` : "0/0"}
            </span>
          </div>
          <div className="p-4 bg-white">
            <div className="flex justify-between items-end mb-2">
              <span className="text-[12px] font-medium text-gray-600">Overall Completion</span>
              <span className="text-[12px] font-bold text-[#054334]">
                {activeChecklist ? Math.round(activeChecklist.progress_percentage) : 0}%
              </span>
            </div>
            <div className="w-full h-1.5 bg-gray-200 rounded-full overflow-hidden mb-4">
              <div className="h-full bg-[#10B981] rounded-full" style={{ width: `${activeChecklist ? activeChecklist.progress_percentage : 0}%` }}></div>
            </div>

            <ul className="space-y-2">
              {complianceItems.slice(0, 3).map((item) => {
                if (item.status === "reviewed") {
                  return (
                    <li key={item.document_name} className="flex items-center space-x-2">
                      <div className="w-4 h-4 bg-[#e0f5ee] rounded-full flex items-center justify-center text-[#10B981] flex-shrink-0">
                        <Icons.CheckCircle />
                      </div>
                      <span className="text-[12px] text-gray-400 line-through truncate">{item.document_name}</span>
                    </li>
                  );
                }
                return (
                  <li key={item.document_name} className="flex items-center space-x-2">
                    <span className="w-4 h-4 bg-gray-100 text-gray-400 rounded-full flex items-center justify-center font-black text-[10px] flex-shrink-0">•</span>
                    <span className="text-[12px] text-gray-600 truncate">{item.document_name}</span>
                  </li>
                );
              })}
            </ul>
          </div>
        </div>

        {/* Announcements Section */}
        <div className="bg-white rounded-[16px] shadow-sm mb-6 border border-gray-100 p-5">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-[15px] font-bold text-gray-800">Announcements</h2>
            <button
              onClick={() => navigate("/org/announcements")}
              className="text-[11px] font-bold text-[#086C53] flex items-center hover:underline"
            >
              VIEW ALL
            </button>
          </div>
          <div className="space-y-3">
            {announcements.length === 0 ? (
              <p className="text-gray-500 text-[12px] text-center py-4">No recent announcements.</p>
            ) : (
              announcements.slice(0, 3).map((ann) => (
                <div key={ann.announcement_id} className="p-3 bg-gray-50 rounded-xl border border-gray-100">
                  {ann.image_path && (
                    <img
                      src={getAnnouncementImageUrl(ann)}
                      alt={ann.title}
                      className="w-full h-24 object-cover rounded-lg mb-2 border border-gray-100"
                    />
                  )}
                  <div className="flex justify-between items-start mb-1">
                    <h3 className="font-bold text-[13px] text-gray-800 truncate pr-2">{ann.title}</h3>
                    <span className="text-[9px] text-gray-400 font-medium whitespace-nowrap">
                      {new Date(ann.created_at).toLocaleDateString()}
                    </span>
                  </div>
                  <p className="text-[11px] text-gray-500 line-clamp-2 leading-relaxed">{ann.content}</p>
                </div>
              ))
            )}
          </div>
        </div>

      </div>
    </div>
  );
};

export default OrgDashboardMobile;
